import { inject, reactive, Ref, ref, toRefs, watch, watchEffect } from 'vue'
import { Tree, TreeNode } from '@/components/tree/tree'
import Message from '@/components/message'
import {
  convertObjectPropsToArray,
  isIterableNode,
} from '@/components/tree/utils'
import { t } from '@/plugins/i18n'

export const useTree = (_props, emit) => {
  const props = toRefs(_props)
  const tree = ref(createTree(props.children.value))
  const isExpanded = ref(false)
  const isLoadingSearchResults = ref(false)
  const searchValue = reactive({
    value: inject('searchEmitValue', '') || '',
  })
  const results: Ref<TreeNode[] | null> = ref(null)
  const selectedNode = ref('')

  let onSearchEmitTimeout: NodeJS.Timeout

  function _convertToTreeNode(node: unknown, parent = null): TreeNode {
    const [key, value] =
      Object.entries(node).length > 1
        ? [parent, convertObjectPropsToArray(node)]
        : Object.entries(node).flat()
    const parentId = parent ? parent + key : key
    return {
      parentId: parent,
      id: parentId,
      key: parentId,
      [props.trackBy.value]: value,
      [props.labelBy.value]: key,
      checked: false,
      children:
        (value && typeof value === 'string') || value === null
          ? []
          : isIterableNode(value)
            ? value
            : Object.entries(value).map(([k, v]) =>
                _convertToTreeNode({ [k]: v }, parentId),
              ),
    }
  }

  function createTreeNode(
    _nodes: [] | string | object,
    tree: Tree,
    parent = null,
  ) {
    if (typeof _nodes === 'string') {
      return
    }
    const nodes = isIterableNode(_nodes)
      ? _nodes
      : convertObjectPropsToArray(_nodes)
    for (const [index, _node] of nodes.entries()) {
      const newNode =
        _node instanceof Object && ('id' in _node || 'parentId' in _node)
          ? _node
          : Array.isArray(_node)
            ? _node.map((n) => _convertToTreeNode(n, index))
            : _convertToTreeNode(_node, parent)
      tree.insert(
        newNode.parentId,
        newNode.id,
        newNode[props.trackBy.value],
        newNode[props.labelBy.value],
        newNode.checked,
        newNode,
        nodes[index][props.trackBy.value] ?? nodes[index],
      )
      if (newNode[props.childrenBy.value]?.length) {
        createTreeNode(newNode[props.childrenBy.value], tree, newNode.id)
      }
    }
  }

  function createTree(data: Record<string, unknown>): Tree {
    const tree = new Tree(null, '_root')
    createTreeNode(data, tree)
    return tree
  }

  function formatTraversalLabel(stringValue: string) {
    const tokens = stringValue.split('>')
    tokens[tokens.length - 1] =
      `<span class="text-black-40">${tokens[tokens.length - 1]}</span>`
    return tokens.join(' > ')
  }

  function traversalLabel(option: TreeNode | undefined): string {
    if (!option) return ''
    if (option && option.parentId !== null) {
      return `${traversalLabel(tree.value.findKey(option.parentId))} > ${
        option.label
      }`
    }
    return option.label
  }

  function createClojureThreadFirstReferenceToNodeValue(
    node: TreeNode,
    addInitialToken = true,
  ): string {
    if (node && node.parentId !== null) {
      return `${addInitialToken ? '-> ' : ''}${createClojureThreadFirstReferenceToNodeValue(tree.value.findKey(node.parentId), false)} :${
        node.label
      }`
    }
    return node.label
  }

  const clearInput = () => (searchValue.value = '')

  const onSearchEmit = () => {
    if (searchValue.value === '') return
    isLoadingSearchResults.value = true
    clearTimeout(onSearchEmitTimeout)
    onSearchEmitTimeout = setTimeout(() => {
      results.value = tree.value.findFuzzy(searchValue.value?.toLowerCase())
      isLoadingSearchResults.value = false
    }, 500)
    expandAll()
  }

  const toggleChildren = (option: Record<string, unknown>, toggle: boolean) => {
    for (const child of option[props.childrenBy.value]) {
      child.checked = toggle
      if (child.hasChildren) {
        toggleChildren(child, toggle)
      }
    }
  }

  const expandAll = () => {
    tree.value.expandAll()
    isExpanded.value = true
  }

  const collapseAll = () => {
    tree.value.collapseAll()
    isExpanded.value = false
  }

  const handleNodeSelect = async (e: TreeNode) => {
    selectedNode.value = traversalLabel(e)
    const clojureValue = `(${createClojureThreadFirstReferenceToNodeValue(e)})`
    await navigator.clipboard.writeText(clojureValue)
    emit('node-select', e)
    Message.success({
      text: t('LpTree.ValueCopied'),
      duration: 2000,
      show: true,
    })
  }

  watchEffect(() => {
    if (searchValue.value === '' || searchValue.value === null) {
      results.value = null
    }
  })

  watch(
    () => tree.value.root[props.childrenBy.value],
    () => {
      emit('update:modelValue', tree.value.root[props.childrenBy.value])
    },
    { deep: true },
  )

  return {
    tree,
    results,
    searchValue,
    isLoadingSearchResults,
    isExpanded,
    selectedNode,
    traversalLabel,
    clearInput,
    onSearchEmit,
    expandAll,
    collapseAll,
    toggleChildren,
    createClojureThreadFirstReferenceToNodeValue,
    handleNodeSelect,
    formatTraversalLabel,
  }
}
